<template>
  <div class="column content-box">
    <woot-button
      color-scheme="success"
      class-names="button--fixed-top"
      icon="add-circle"
      @click="openAddPopup()"
    >
      {{ $t('AUTOMATION.HEADER_BTN_TXT') }}
    </woot-button>
    <woot-tabs
      :index="activeCategoryIndex"
      class="automation-category-tabs"
      @change="onCategoryChange"
    >
      <woot-tabs-item
        v-for="category in triggerCategories"
        :key="category.key"
        :name="
          $t(`AUTOMATION.TRIGGER_CATEGORIES.${category.key.toUpperCase()}`)
        "
        :show-badge="false"
      />
    </woot-tabs>
    <div class="row automation-search-bar">
      <div class="small-6 columns">
        <div
          class="search-container"
          :class="{
            'no-text-input': ['event', 'trigger', 'action'].includes(
              filters.searchField
            ),
          }"
        >
          <div class="search-field-select">
            <select v-model="filters.searchField">
              <option value="name">
                {{ $t('AUTOMATION.SEARCH_FIELDS.NAME') }}
              </option>
              <option value="description">
                {{ $t('AUTOMATION.SEARCH_FIELDS.DESCRIPTION') }}
              </option>
              <option value="event">
                {{ $t('AUTOMATION.SEARCH_FIELDS.EVENT') }}
              </option>
              <option value="trigger">
                {{ $t('AUTOMATION.SEARCH_FIELDS.TRIGGER') }}
              </option>
              <option value="action">
                {{ $t('AUTOMATION.SEARCH_FIELDS.ACTION') }}
              </option>
            </select>
            <select
              v-if="filters.searchField === 'event'"
              v-model="filters.searchQuery"
              @change="applySearch"
            >
              <option
                v-for="event in availableEvents"
                :key="event.key"
                :value="event.key"
              >
                {{ event.label }}
              </option>
            </select>
            <select
              v-if="filters.searchField === 'trigger'"
              v-model="filters.searchQuery"
              @change="applySearch"
            >
              <option
                v-for="trigger in availableTriggers"
                :key="trigger.key"
                :value="trigger.key"
              >
                {{ trigger.label }}
              </option>
            </select>
            <select
              v-if="filters.searchField === 'action'"
              v-model="filters.searchQuery"
              @change="applySearch"
            >
              <option
                v-for="action in availableActions"
                :key="action.key"
                :value="action.key"
              >
                {{ action.label }}
              </option>
            </select>
          </div>

          <input
            v-if="!['event', 'trigger', 'action'].includes(filters.searchField)"
            v-model="filters.searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('AUTOMATION.SEARCH_PLACEHOLDER')"
            @keyup.enter="applySearch"
          />

          <button class="search-button" @click="applySearch">
            <i class="ion-ios-search"></i>
          </button>
        </div>
        <div class="automation-status-filter">
          <label>
            <input type="checkbox" v-model="filters.onlyInactive" />
            {{ $t('AUTOMATION.SEARCH_FIELDS.ONLY_ACTIVE') }}
          </label>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="small-8 columns with-right-space">
        <p
          v-if="!uiFlags.isFetching && !categoryRecords.length"
          class="no-items-error-message"
        >
          {{ $t('AUTOMATION.LIST.404') }}
        </p>
        <woot-loading-state
          v-if="uiFlags.isFetching"
          :message="$t('AUTOMATION.LOADING')"
        />
        <table
          v-if="!uiFlags.isFetching && categoryRecords.length"
          class="woot-table"
        >
          <thead>
            <th
              v-for="thHeader in $t('AUTOMATION.LIST.TABLE_HEADER')"
              :key="thHeader"
            >
              {{ thHeader }}
            </th>
          </thead>
          <tbody>
            <tr v-for="(automation, index) in filteredRecords" :key="index">
              <td>{{ automation.name }}</td>
              <td>{{ automation.description }}</td>
              <td>
                <woot-switch
                  :value="automation.active"
                  @input="toggleAutomation(automation, automation.active)"
                />
              </td>
              <td>{{ readableTime(automation.created_on) }}</td>
              <td class="button-wrapper">
                <woot-button
                  v-tooltip.top="$t('AUTOMATION.FORM.EDIT')"
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  class-names="grey-btn"
                  :is-loading="loading[automation.id]"
                  icon="edit"
                  @click="openEditPopup(automation)"
                />
                <woot-button
                  v-tooltip.top="$t('AUTOMATION.CLONE.TOOLTIP')"
                  variant="smooth"
                  size="tiny"
                  color-scheme="primary"
                  class-names="grey-btn"
                  :is-loading="loading[automation.id]"
                  icon="copy"
                  @click="cloneAutomation(automation.id)"
                />
                <woot-button
                  v-tooltip.top="$t('AUTOMATION.FORM.DELETE')"
                  variant="smooth"
                  color-scheme="alert"
                  size="tiny"
                  icon="dismiss-circle"
                  class-names="grey-btn"
                  :is-loading="loading[automation.id]"
                  @click="openDeletePopup(automation, index)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="small-4 columns">
        <span v-dompurify-html="$t('AUTOMATION.SIDEBAR_TXT')" />
      </div>
    </div>
    <woot-modal
      :show.sync="showAddPopup"
      size="medium"
      :on-close="hideAddPopup"
    >
      <add-automation-rule
        v-if="showAddPopup"
        :on-close="hideAddPopup"
        :initial-category="activeCategory"
        @saveAutomation="submitAutomation"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <woot-modal
      :show.sync="showEditPopup"
      size="medium"
      :on-close="hideEditPopup"
    >
      <edit-automation-rule
        v-if="showEditPopup"
        :on-close="hideEditPopup"
        :selected-response="selectedResponse"
        @saveAutomation="submitAutomation"
      />
    </woot-modal>
    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import AddAutomationRule from './AddAutomationRule.vue';
import EditAutomationRule from './EditAutomationRule.vue';
import alertMixin from 'shared/mixins/alertMixin';
import timeMixin from 'dashboard/mixins/time';
import {
  AUTOMATION_TRIGGER_CATEGORIES,
  getAutomationTriggerCategory,
} from './constants/events';

export default {
  components: {
    AddAutomationRule,
    EditAutomationRule,
  },
  mixins: [alertMixin, timeMixin],
  data() {
    return {
      filters: {
        searchQuery: '',
        appliedSearchQuery: '',
        searchField: 'name',
        onlyInactive: false,
      },
      activeCategory: AUTOMATION_TRIGGER_CATEGORIES[0].key,
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedResponse: {},
      toggleModalTitle: this.$t('AUTOMATION.TOGGLE.ACTIVATION_TITLE'),
      toggleModalDescription: this.$t(
        'AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION'
      ),
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'automations/getUIFlags',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    triggerCategories() {
      const automations =
        this.$store.getters['automations/getAutomations'] || [];
      return AUTOMATION_TRIGGER_CATEGORIES.filter(
        category =>
          !category.featureFlag ||
          this.isFeatureEnabledonAccount(
            this.accountId,
            category.featureFlag
          ) ||
          automations.some(
            automation =>
              getAutomationTriggerCategory(automation.event_name) ===
              category.key
          )
      );
    },
    activeCategoryIndex() {
      return Math.max(
        this.triggerCategories.findIndex(
          category => category.key === this.activeCategory
        ),
        0
      );
    },
    categoryAutomations() {
      const automations =
        this.$store.getters['automations/getAutomations'] || [];
      return automations.filter(this.isInActiveCategory);
    },
    categoryRecords() {
      return this.records.filter(this.isInActiveCategory);
    },
    records() {
      const automations =
        this.$store.getters['automations/getAutomations'] || [];

      return [...automations].sort((a, b) => {
        const nameA = (a.name || '').trim();
        const nameB = (b.name || '').trim();

        return nameA.localeCompare(nameB, 'pt-BR', {
          sensitivity: 'base',
        });
      });
    },
    availableEvents() {
      const automations = this.categoryAutomations;

      const events = automations.map(a => a.event_name);

      const unique = [...new Set(events)];

      return unique
        .map(key => ({
          key,
          label: this.translateEvent(key),
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })
        );
    },
    availableActions() {
      const automations = this.categoryAutomations;

      const actions = automations.flatMap(a =>
        (a.actions || []).map(act => act.action_name)
      );

      const unique = [...new Set(actions)];

      return unique
        .map(key => ({
          key,
          label: this.translateAction(key),
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })
        );
    },
    filteredRecords() {
      const { appliedSearchQuery, searchField, onlyInactive } = this.filters;

      let list = [...this.categoryRecords];

      if (onlyInactive) {
        list = list.filter(item => item.active);
      }

      if (!appliedSearchQuery) {
        return list;
      }

      const query = appliedSearchQuery.toLowerCase().trim();

      return list.filter(item => {
        if (searchField === 'event') {
          return (item.event_name || '').toLowerCase().includes(query);
        }
        if (searchField === 'trigger') {
          return (item.conditions || []).some(condition =>
            (condition.attribute_key || '').toLowerCase().includes(query)
          );
        }

        if (searchField === 'action') {
          return (item.actions || []).some(action =>
            (action.action_name || '').toLowerCase().includes(query)
          );
        }

        const value = (item[searchField] || '').toLowerCase();
        return value.includes(query);
      });
    },
    // Delete Modal
    deleteConfirmText() {
      return `${this.$t('AUTOMATION.DELETE.CONFIRM.YES')} ${
        this.selectedResponse.name
      }`;
    },
    deleteRejectText() {
      return `${this.$t('AUTOMATION.DELETE.CONFIRM.NO')} ${
        this.selectedResponse.name
      }`;
    },
    deleteMessage() {
      return ` ${this.selectedResponse.name}?`;
    },
    availableTriggers() {
      const automations = this.categoryAutomations;

      const triggers = automations.flatMap(a =>
        (a.conditions || []).map(c => c.attribute_key)
      );

      const unique = [...new Set(triggers)];

      return unique
        .map(key => ({
          key,
          label: this.translateTrigger(key),
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })
        );
    },
  },
  watch: {
    'filters.searchField'(newVal) {
      if (newVal === 'event') {
        if (this.availableEvents.length) {
          this.filters.searchQuery = this.availableEvents[0].key;
          this.filters.appliedSearchQuery = this.availableEvents[0].key;
        }
      } else if (newVal === 'trigger') {
        if (this.availableTriggers.length) {
          this.filters.searchQuery = this.availableTriggers[0].key;
          this.filters.appliedSearchQuery = this.availableTriggers[0].key;
        }
      } else if (newVal === 'action') {
        if (this.availableActions.length) {
          this.filters.searchQuery = this.availableActions[0].key;
          this.filters.appliedSearchQuery = this.availableActions[0].key;
        }
      } else {
        this.filters.searchQuery = '';
        this.filters.appliedSearchQuery = '';
      }
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('agents/get');
    this.$store.dispatch('contacts/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('labels/get');
    this.$store.dispatch('macros/get');
    this.$store.dispatch('campaigns/getCampaignWhatsapp', {
      page: 1,
      sortAttr: '-created_at',
    });
    this.$store.dispatch('automations/get');
    this.$store.dispatch('closingReasons/get', { page: 1, limit: 100 });
  },
  methods: {
    openAddPopup() {
      this.showAddPopup = true;
    },
    isInActiveCategory(automation) {
      return (
        getAutomationTriggerCategory(automation.event_name) ===
        this.activeCategory
      );
    },
    onCategoryChange(index) {
      const category = this.triggerCategories[index];
      if (!category || category.key === this.activeCategory) return;

      this.activeCategory = category.key;
      this.filters.searchField = 'name';
      this.filters.searchQuery = '';
      this.filters.appliedSearchQuery = '';
    },
    translateTrigger(key) {
      if (!key) return '';
      return this.$t(`CONDITIONS.${key.toUpperCase()}`);
    },
    translateEvent(key) {
      if (!key) return '';
      return this.$t(
        `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS.${key.toUpperCase()}`
      );
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    translateAction(key) {
      if (!key) return '';
      return this.$t(`CONVERSATION.CARD_CONTEXT_MENU.${key.toUpperCase()}`);
    },
    openEditPopup(response) {
      this.selectedResponse = response;
      this.showEditPopup = true;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedResponse.id] = true;
      this.closeDeletePopup();
      this.deleteAutomation(this.selectedResponse.id);
    },
    async deleteAutomation(id) {
      try {
        await this.$store.dispatch('automations/delete', id);
        this.showAlert(this.$t('AUTOMATION.DELETE.API.SUCCESS_MESSAGE'));
        this.loading[this.selectedResponse.id] = false;
      } catch (error) {
        this.showAlert(this.$t('AUTOMATION.DELETE.API.ERROR_MESSAGE'));
      }
    },
    async cloneAutomation(id) {
      try {
        await this.$store.dispatch('automations/clone', id);
        this.showAlert(this.$t('AUTOMATION.CLONE.API.SUCCESS_MESSAGE'));
        this.$store.dispatch('automations/get');
        this.loading[this.selectedResponse.id] = false;
      } catch (error) {
        this.showAlert(this.$t('AUTOMATION.CLONE.API.ERROR_MESSAGE'));
      }
    },
    async submitAutomation(payload, mode) {
      try {
        const action =
          mode === 'edit' ? 'automations/update' : 'automations/create';
        const successMessage =
          mode === 'edit'
            ? this.$t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE')
            : this.$t('AUTOMATION.ADD.API.SUCCESS_MESSAGE');
        await this.$store.dispatch(action, payload);
        this.showAlert(successMessage);
        if (mode !== 'edit') {
          this.activeCategory = getAutomationTriggerCategory(
            payload.event_name
          );
        }
        this.hideAddPopup();
        this.hideEditPopup();
      } catch (error) {
        const errorMessage =
          mode === 'edit'
            ? this.$t('AUTOMATION.EDIT.API.ERROR_MESSAGE')
            : this.$t('AUTOMATION.ADD.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
    async toggleAutomation(automation, status) {
      try {
        this.toggleModalTitle = status
          ? this.$t('AUTOMATION.TOGGLE.DEACTIVATION_TITLE')
          : this.$t('AUTOMATION.TOGGLE.ACTIVATION_TITLE');
        this.toggleModalDescription = status
          ? this.$t('AUTOMATION.TOGGLE.DEACTIVATION_DESCRIPTION', {
              automationName: automation.name,
            })
          : this.$t('AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION', {
              automationName: automation.name,
            });
        // Check if user confirms to proceed
        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          await await this.$store.dispatch('automations/update', {
            id: automation.id,
            active: !status,
          });
          const message = status
            ? this.$t('AUTOMATION.TOGGLE.DEACTIVATION_SUCCESFUL')
            : this.$t('AUTOMATION.TOGGLE.ACTIVATION_SUCCESFUL');
          this.showAlert(message);
        }
      } catch (error) {
        this.showAlert(this.$t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
      }
    },
    readableTime(date) {
      return this.messageStamp(new Date(date), 'dd/MM/yy - HH:mm');
    },
    applySearch() {
      this.filters.appliedSearchQuery = this.filters.searchQuery;
    },
  },
};
</script>

<style scoped>
.automation__status-checkbox {
  margin: 0;
}

.content-box {
  padding-bottom: 6rem;
}

.automation-category-tabs {
  margin-bottom: var(--space-normal);
}

.automation-search-bar {
  margin-bottom: 1rem;
}

.search-container {
  display: flex;
  align-items: center;
  width: 600px;
  height: 38px;
  background: #f5f6f7;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e2e2e2;
}

.search-field-select {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 8px;
  position: relative;
  flex-shrink: 0;
}

.search-field-select select {
  border: none;
  background: transparent;
  font-size: 13px;
  color: #2d3748;
  cursor: pointer;
  outline: none;
  height: 100%;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 0 18px 0 4px;
  background-image: url("data:image/svg+xml;utf8,<svg fill='%232d3748' height='10' viewBox='0 0 20 20' width='10' xmlns='http://www.w3.org/2000/svg'><path d='M5 7l5 5 5-5z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 4px center;
  margin-top: 15px;
}

.search-field-select select + select {
  margin-left: 8px;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  height: 100%;
  margin: 0 6px;
  padding: 0 10px;
  font-size: 13px;
  color: #4a5568;
}

.search-button {
  width: 40px;
  height: 100%;
  background: #1f73d8;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
}

.search-button i {
  font-size: 15px;
}

.automation-status-filter {
  margin-top: 2px;
  font-size: 13px;
  color: #4a5568;
}

.automation-status-filter input {
  margin-right: 6px;
}

.search-container.no-text-input {
  width: 420px;
}

.search-container.no-text-input .search-field-select {
  flex: 1;
}

.search-container.no-text-input .search-field-select select {
  width: 100%;
}
</style>
